import { loadStripe } from '@stripe/stripe-js';
import axios from 'axios';
import { STRIPE_CONFIG, API_BASE_URL } from '../config/stripe';
import { DEFAULT_TRIAL_PERIOD_DAYS } from '../config/config';

// Initialize Stripe
const stripePromise = loadStripe(STRIPE_CONFIG.publishableKey);

interface CreateCheckoutSessionParams {
  priceId: string;
  couponCode?: string;
  cancelUrl?: string;
  successUrl?: string;
  trialDays?: number;
  customerEmail?: string;
}

interface CreateCheckoutSessionResponse {
  sessionId: string;
}

export const stripeService = {
  // Create a Stripe checkout session
  async createCheckoutSession(params: CreateCheckoutSessionParams): Promise<string> {
    try {
      const { 
        priceId, 
        couponCode, 
        cancelUrl = window.location.origin,
        successUrl = `${window.location.origin}/success/{CHECKOUT_SESSION_ID}`,
        customerEmail
      } = params;
      
      // Prepare request body
      const requestBody: any = {
        priceId,
        cancelUrl,
        successUrl,
        trialDays: params.trialDays ?? DEFAULT_TRIAL_PERIOD_DAYS
      };

      // Add customer email if provided
      if (customerEmail) {
        requestBody.customerEmail = customerEmail;
      }

      // If coupon code provided, validate it first
      if (couponCode) {
        const couponResponse = await axios.get(`${API_BASE_URL}/coupon`, {
          params: { code: couponCode }
        });
        
        if (couponResponse.data.coupon) {
          requestBody.coupon = couponResponse.data.coupon;
        }
      }

      // Create checkout session
      const response = await axios.post<CreateCheckoutSessionResponse>(
        `${API_BASE_URL}/subscribeSession`,
        requestBody
      );

      return response.data.sessionId;
    } catch (error) {
      console.error('Error creating checkout session:', error);
      throw error;
    }
  },

  // Redirect to Stripe checkout
  async redirectToCheckout(sessionId: string): Promise<void> {
    const stripe = await stripePromise;
    
    if (!stripe) {
      throw new Error('Stripe failed to load');
    }

    const { error } = await stripe.redirectToCheckout({ sessionId });
    
    if (error) {
      console.error('Error redirecting to checkout:', error);
      throw error;
    }
  },

  // Verify a subscription after successful checkout
  async verifySubscription(sessionId: string): Promise<any> {
    try {
      const response = await axios.get(`${API_BASE_URL}/sessionSubscription`, {
        params: { sessionId }
      });
      
      return response.data;
    } catch (error) {
      console.error('Error verifying subscription:', error);
      throw error;
    }
  },

  // Get subscription details
  async getSubscription(subscriptionId: string): Promise<any> {
    try {
      const response = await axios.get(`${API_BASE_URL}/subscription`, {
        params: { id: subscriptionId }
      });
      
      return response.data;
    } catch (error) {
      console.error('Error getting subscription:', error);
      throw error;
    }
  }
};